import { useState } from 'react';
import { isPlatformApiError } from '@traceio-org/platform-client';
import type { TicketAttachment, TicketEvent, TicketMessage } from '@traceio-org/platform-contracts';
import { DateTimeText } from '../../components/records';
import { useAdmin } from '../../context';
import { MESSAGE_KIND_TAGS, describeEvent } from './ticketFormat';

/** True for platform-api's answer that this environment has no attachment storage (503 attachments_unavailable). */
export function isAttachmentsOff(error: unknown): boolean {
  return (
    isPlatformApiError(error) && error.kind === 'http' && error.problem.reason === 'attachments_unavailable'
  );
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * An attachment of a message: a button that asks platform-api for a short-lived download URL and opens it (images
 * inline, PDFs and CSVs as downloads). Without attachment storage in this environment it says so instead.
 */
function AttachmentButton({ attachment }: { attachment: TicketAttachment }) {
  const { api } = useAdmin();
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    // The tab opens inside the click (before any await), so a popup blocker lets it through; it gets the URL once
    // platform-api signs one, and closes again if that fails. opener is cut so the file's page cannot reach back.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const { url } = await api.ticketAttachmentDownload(attachment.id);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      tab?.close();
      console.warn('admin-web: an attachment download URL was refused', error);
      setProblem(
        isAttachmentsOff(error)
          ? 'Attachments are off in this environment.'
          : 'Could not open it. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <li>
      <button
        type="button"
        className="admin-attachment"
        onClick={() => void open()}
        aria-busy={busy || undefined}
      >
        <span>{attachment.fileName}</span>
        <span className="admin-muted">{formatSize(attachment.sizeBytes)}</span>
      </button>
      {problem ? (
        <span className="admin-field__error" role="alert">
          {problem}
        </span>
      ) : null}
    </li>
  );
}

type Entry =
  { kind: 'message'; at: string; message: TicketMessage } | { kind: 'event'; at: string; event: TicketEvent };

/** Messages and timeline rows in one list, oldest first (a change made with a message follows it). */
export function activityOf(messages: readonly TicketMessage[], events: readonly TicketEvent[]): Entry[] {
  const entries: Entry[] = [
    ...messages.map((message) => ({ kind: 'message' as const, at: message.createdAt, message })),
    ...events.map((event) => ({ kind: 'event' as const, at: event.at, event })),
  ];
  // Stable sort: equal times keep messages before the changes recorded with them.
  return entries.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

/**
 * The ticket's activity, oldest first: messages and the field-change timeline interleaved. Each message says who
 * reads it: a reply (the customer sees it), a Trace note (only Trace: on a warm surface, so it is never mistaken for a
 * reply) or an event; each change is one quiet line (who changed what, from what to what). Bodies are plain text:
 * platform-api sanitizes them and nothing here renders HTML.
 */
export function TicketThread({
  messages,
  events = [],
}: {
  messages: readonly TicketMessage[];
  events?: readonly TicketEvent[];
}) {
  const entries = activityOf(messages, events);
  if (entries.length === 0) return <p className="admin-muted">No message yet.</p>;
  return (
    <ol className="admin-bare-list admin-ticket-thread" aria-label="Activity">
      {entries.map((entry) =>
        entry.kind === 'event' ? (
          <li key={entry.event.id} className="admin-ticket-change" data-field={entry.event.field}>
            <span className="admin-ticket-change__dot" aria-hidden="true" />
            <span>
              <strong>
                {entry.event.actor.name ?? (entry.event.actor.kind === 'system' ? 'Trace' : 'Someone')}
              </strong>{' '}
              {describeEvent(entry.event)}
            </span>
            <span className="admin-ticket-message__when">
              <DateTimeText iso={entry.event.at} />
            </span>
          </li>
        ) : (
          <li key={entry.message.id} className="admin-ticket-message" data-kind={entry.message.kind}>
            <p className="admin-ticket-message__head">
              <strong>{entry.message.author.name ?? 'Trace'}</strong>
              <span className="admin-ticket-message__tag">{MESSAGE_KIND_TAGS[entry.message.kind]}</span>
              <span className="admin-ticket-message__when">
                <DateTimeText iso={entry.message.createdAt} />
              </span>
            </p>
            <p className="admin-ticket-message__body">{entry.message.body}</p>
            {entry.message.attachments.length > 0 ? (
              <ul className="admin-bare-list admin-ticket-attachments" aria-label="Attachments">
                {entry.message.attachments.map((attachment) => (
                  <AttachmentButton key={attachment.id} attachment={attachment} />
                ))}
              </ul>
            ) : null}
          </li>
        ),
      )}
    </ol>
  );
}
